import type { PdiLibrary, PdiNode } from './types.js';

/** Sayfa başına kontrol noktası (PMS kütüphanesinin tablo kalıbı). */
export const PAGE_SIZE = 25;

export type StatusFilter = 'all' | 'active' | 'inactive';

export interface AreaBranch {
  area: PdiNode;
  systems: { system: PdiNode; count: number }[];
  count: number;
}

const byOrder = (a: PdiNode, b: PdiNode) =>
  a.sortOrder - b.sortOrder || a.code.localeCompare(b.code);

/** Sol ağaç: alan → sistem, altındaki kontrol noktası sayısıyla (pasifler dahil). */
export function areaTree(nodes: readonly PdiNode[]): AreaBranch[] {
  const counts = new Map<string, number>();
  for (const node of nodes) {
    if (node.level === 'checkpoint' && node.parentCode) {
      counts.set(node.parentCode, (counts.get(node.parentCode) ?? 0) + 1);
    }
  }
  return nodes
    .filter((n) => n.level === 'area')
    .sort(byOrder)
    .map((area) => {
      const systems = nodes
        .filter((n) => n.level === 'system' && n.parentCode === area.code)
        .sort(byOrder)
        .map((system) => ({ system, count: counts.get(system.code) ?? 0 }));
      return { area, systems, count: systems.reduce((sum, s) => sum + s.count, 0) };
    });
}

/**
 * Seçili alan ya da sistemin kontrol noktaları; arama kimlik, ad ve kapsamda (büyük/küçük harf
 * duyarsız); durum süzgeci.
 */
export function filterCheckpoints(
  library: PdiLibrary,
  input: { selected: string | null; query: string; status: StatusFilter },
): PdiNode[] {
  const query = input.query.trim().toLocaleLowerCase();
  const selected = input.selected;
  return library.nodes
    .filter((n) => n.level === 'checkpoint')
    .filter((n) => selected === null || n.code.startsWith(`${selected}-`))
    .filter((n) => input.status === 'all' || (input.status === 'active') === n.active)
    .filter(
      (n) =>
        query === '' ||
        n.code.toLocaleLowerCase().includes(query) ||
        n.name.toLocaleLowerCase().includes(query) ||
        (n.content?.scope ?? '').toLocaleLowerCase().includes(query),
    )
    .sort((a, b) => a.code.localeCompare(b.code));
}

export function pagesOf(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

export function pageOf<T>(rows: readonly T[], page: number): T[] {
  return rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
}

/** Sunucunun `path:CODE,…` ayrıntısı. */
export function issuesOf(detail: string): { path: string; code: string }[] {
  return detail
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const at = part.lastIndexOf(':');
      return at < 0
        ? { path: part, code: 'INVALID' }
        : { path: part.slice(0, at), code: part.slice(at + 1) };
    });
}
